import { getCapturedLogs } from "./logBuffer";
import { ackCommand } from "./commands";
import { captureDone, captureStage } from "./captureTrail";
import {
  captureTizenScreenshot,
  nativeLogMode,
  nativeScreenshotMode,
} from "./foyerBridge";

/**
 * TV-side diagnostics capture + upload.
 *
 * Both flows end the same way: capture bytes, POST them to the command's upload
 * endpoint, which stores the asset and acks the command server-side (the upload
 * IS the ack). Screen-token-gated; best-effort - a capture/upload failure just
 * leaves the command open for the sweep to retire.
 *
 * ## Two capture paths, and why both exist
 *
 * **Native (preferred where the box has it).** A real framebuffer grab and a real
 * `logcat` tail, via the Android shell's `requestScreenshot` / `requestLogs`
 * handshake (see `android/.../DiagnosticsProvider.kt`). This is the one that can
 * answer the question support is actually asking, because it shows the DISPLAY:
 * another app in the foreground after a tile press or a cast, a firmware dialog or
 * OTA prompt covering us, a white screen after a WebView renderer crash, a black
 * panel. And the native log spans process lifetimes, so it carries the fatal trace
 * of the crash that killed the last one.
 *
 * **In-app (the floor, always available).** `html2canvas` over our own DOM, plus
 * the console ring buffer. It works on web, the CMS preview and any shell without
 * a native route - and it is honest about what it is: a picture of what the page
 * BELIEVES it is showing. On a broken box that is a picture of a healthy TV, which
 * is precisely why the native path had to exist. Kept as the fallback because a
 * screenshot of the right thing is better than nothing at all, and because the
 * fleet console labels the two differently (`screenshotNative` capability), so
 * nobody is misled about which one they are looking at.
 *
 * Logs are not either/or: when a native tail is available it is delivered
 * ALONGSIDE the console buffer, because they are complementary. The shell forwards
 * only WARN/ERROR to logcat, so the ring buffer holds web-level detail logcat
 * never saw; and the ring buffer is memory-only, so logcat holds everything from
 * before the current page. One file, two clearly-headed sections.
 */

interface UploadArgs {
  screenId: string;
  commandId: string;
  screenToken: string;
  apiBase?: string;
}

/**
 * How long to wait for a native capture before falling back to the in-app path.
 *
 * Generous on purpose: an accessibility screenshot on a busy 4K panel, plus
 * PNG encoding on an Amlogic CPU, is not instant. But it IS bounded - a shell that
 * never fires its completion event must degrade to a DOM render, not leave the
 * command open until the server-side sweep retires it hours later.
 */
const NATIVE_CAPTURE_TIMEOUT_MS = 10_000;

/**
 * Upper bounds for the two web-side capture steps that used to be unbounded.
 *
 * Reading back the file Samsung's `captureScreen` wrote goes through
 * `tizen.filesystem` callbacks; a firmware that never calls either one left the
 * promise pending forever, with no fallback and no ack. html2canvas clones and
 * re-lays-out the whole document, which on a signage SoC with a video playing
 * can take a very long time. Either way the command must END - as an image or
 * as an error - rather than sit at "sent".
 */
const TIZEN_READ_TIMEOUT_MS = 10_000;
const DOM_CAPTURE_TIMEOUT_MS = 30_000;

/** Resolve `fallback` if `p` has not settled within `ms`. Never rejects. */
function within<T>(
  p: Promise<T>,
  ms: number,
  fallback: T,
  what: string
): Promise<T> {
  return new Promise<T>((resolve) => {
    const timer = setTimeout(() => {
      console.warn(`[diagnostics] ${what} did not complete in ${ms}ms`);
      resolve(fallback);
    }, ms);
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      () => {
        clearTimeout(timer);
        resolve(fallback);
      }
    );
  });
}

/** Reports which capture step is running, so a page that dies mid-capture can
 *  say where on its next boot (see captureTrail.ts). */
type StageFn = (stage: string) => void;
const noStage: StageFn = () => {};

/**
 * A capture the fleet can label. `mode` is what the device actually used, so the
 * upload can be attributed rather than guessed at.
 */
export interface Capture {
  blob: Blob;
  /** "accessibility" | "screencap" | "legacy" | "in-app" */
  mode: string;
}

/** Decode a base64 image (data URL or bare base64) into a Blob. */
function base64ImageToBlob(encoded: string): Blob | null {
  const comma = encoded.indexOf(",");
  const isDataUrl = encoded.startsWith("data:") && comma >= 0;
  const raw = isDataUrl ? encoded.slice(comma + 1) : encoded;
  // Read the mime type off the data URL rather than assuming PNG: the native path
  // re-encodes a photographic frame as JPEG when PNG blows its size budget, and a
  // JPEG served as image/png renders in a browser but is a lie in the asset store.
  const declared = isDataUrl
    ? /^data:(image\/[a-z0-9.+-]+)/i.exec(encoded)?.[1]
    : undefined;
  const type = declared ? declared.toLowerCase() : "image/png";
  try {
    const bytes = atob(raw);
    if (!bytes.length) return null;
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    return new Blob([arr], { type });
  } catch {
    // Truncated or non-base64 payload: treat as no capture rather than uploading
    // a corrupt asset that looks like a delivered diagnostic.
    return null;
  }
}

/** A short, collision-free id for one native capture request. */
function requestId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Await one native capture over the shell's request/notify/collect handshake.
 *
 * The shell fires `eventName` with `{id, ok, mode}` and holds the payload until we
 * collect it, so a few hundred kilobytes never have to cross as a JS source string
 * (see `MainActivity.notifyCaptureReady`). Resolves null on failure, on a
 * mismatched id, or on timeout - never rejects, and always removes its listener.
 */
function awaitNativeCapture(
  eventName: string,
  start: (id: string) => void,
  collect: (id: string) => string | null | undefined
): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    const id = requestId();
    let settled = false;
    const done = (value: string | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      window.removeEventListener(eventName, onReady as EventListener);
      resolve(value);
    };
    const onReady = (evt: Event) => {
      const detail = (evt as CustomEvent).detail as
        | { id?: string; ok?: boolean }
        | undefined;
      // Ignore a stale event from an earlier, timed-out request: collecting on it
      // would attribute the previous screen to this command.
      if (detail?.id !== id) return;
      if (detail?.ok !== true) {
        done(null);
        return;
      }
      try {
        const payload = collect(id);
        done(
          typeof payload === "string" && payload.length > 0 ? payload : null
        );
      } catch {
        done(null);
      }
    };
    const timer = window.setTimeout(() => {
      console.warn(
        `[diagnostics] native ${eventName} did not complete in ${NATIVE_CAPTURE_TIMEOUT_MS}ms`
      );
      done(null);
    }, NATIVE_CAPTURE_TIMEOUT_MS);
    window.addEventListener(eventName, onReady as EventListener);
    try {
      start(id);
    } catch (err) {
      console.warn("[diagnostics] native capture could not be started", err);
      done(null);
    }
  });
}

/**
 * Capture a screenshot, preferring a real framebuffer grab.
 *
 * Order: the async native handshake, then a legacy synchronous native method if a
 * shell exposes one, then `html2canvas`. Every native failure falls through to the
 * in-app render, because support having a picture of the wrong thing beats having
 * nothing - and the `screenshotNative` capability is what tells them which they
 * got. Returns null only when even the DOM capture is impossible.
 */
export async function captureScreenshot(
  stage: StageFn = noStage
): Promise<Capture | null> {
  const native = typeof window !== "undefined" ? window.FoyerNative : undefined;
  const mode = nativeScreenshotMode();

  if (mode !== "none" && mode !== "legacy" && native?.requestScreenshot) {
    stage("native");
    const encoded = await awaitNativeCapture(
      "foyer:screenshot",
      (id) => native.requestScreenshot!(id),
      (id) => native.takeScreenshotResult?.(id)
    );
    const blob = encoded ? base64ImageToBlob(encoded) : null;
    if (blob) return { blob, mode };
    console.warn(
      "[diagnostics] native screenshot failed - falling back to DOM"
    );
  } else if (mode === "tizen") {
    // Samsung's privileged grab has no shell to hand the result to, so it
    // goes straight through the bridge. Awaited because on this firmware the
    // call writes a JPEG to disk and returns its PATH, which then has to be
    // read back through the widget's filesystem bridge. Null on any surprise
    // in the return contract, which drops us onto the DOM render below -
    // correctly captioned "in-app view only" rather than passed off as the
    // real screen.
    stage("tizen");
    const encoded = await within(
      captureTizenScreenshot(),
      TIZEN_READ_TIMEOUT_MS,
      null,
      "tizen captureScreen"
    );
    const blob = encoded ? base64ImageToBlob(encoded) : null;
    if (blob) return { blob, mode };
    console.warn("[diagnostics] tizen screenshot failed - falling back to DOM");
  } else if (
    mode === "legacy" &&
    typeof native?.captureScreenshot === "function"
  ) {
    try {
      const encoded = native.captureScreenshot();
      const blob =
        typeof encoded === "string" && encoded.length > 0
          ? base64ImageToBlob(encoded)
          : null;
      if (blob) return { blob, mode: "legacy" };
    } catch {
      // fall through to the DOM capture
    }
  }

  stage("dom-render");
  const blob = await within(
    captureDomScreenshot(),
    DOM_CAPTURE_TIMEOUT_MS,
    null,
    "in-app (html2canvas) render"
  );
  return blob ? { blob, mode: "in-app" } : null;
}

/**
 * Render the document with html2canvas (lazy-imported so the library only loads
 * when a screenshot is actually requested). Null on any failure.
 */
async function captureDomScreenshot(): Promise<Blob | null> {
  if (typeof document === "undefined") return null;
  try {
    const { default: html2canvas } = await import("html2canvas");
    const canvas = await html2canvas(document.body, {
      logging: false,
      useCORS: true,
      // Cap the work on big 4K panels - a half-res snapshot is plenty for
      // a diagnostic "what is this TV showing right now".
      scale: Math.min(1, (window.devicePixelRatio || 1) / 2) || 1,
    });
    return await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/png")
    );
  } catch {
    return null;
  }
}

/**
 * Capture recent logs.
 *
 * Both sources when both exist - see the module comment: the native tail carries
 * what happened before this page (a previous crash, an OOM kill, an ANR) and the
 * console buffer carries the web-level detail the shell never forwarded. Neither
 * subsumes the other, so replacing one with the other - which is what this used to
 * do - threw away half the picture.
 */
export async function captureLogs(): Promise<Capture> {
  const native = typeof window !== "undefined" ? window.FoyerNative : undefined;
  const mode = nativeLogMode();
  const sections: string[] = [];

  let nativeText: string | null = null;
  if (mode !== "none" && mode !== "legacy" && native?.requestLogs) {
    nativeText = await awaitNativeCapture(
      "foyer:logs",
      (id) => native.requestLogs!(id),
      (id) => native.takeLogsResult?.(id)
    );
  } else if (mode === "legacy" && typeof native?.captureLogs === "function") {
    try {
      nativeText = native.captureLogs() ?? null;
    } catch {
      nativeText = null;
    }
  }

  if (nativeText) {
    sections.push(
      `########## native device log (${mode}) ##########\n${nativeText}`
    );
  }
  const buffered = getCapturedLogs();
  sections.push(
    "########## in-app console ring buffer ##########\n" +
      (buffered || "(empty - the page has logged nothing since it loaded)")
  );
  if (!nativeText && mode === "none") {
    sections.push(
      "########## note ##########\n" +
        "No native log tail on this device: it has no shell route (plain web, CMS\n" +
        "preview, or an Android shell before kiosk 0.2.41). The buffer above lives\n" +
        "in the page, so it cannot contain a crash that killed the page, and a\n" +
        "force-stop erases it. See docs/native-diagnostics.md."
    );
  }

  return {
    blob: new Blob([sections.join("\n\n")], { type: "text/plain" }),
    mode: nativeText ? mode : "in-app",
  };
}

/**
 * POST a captured blob to the command's upload endpoint (stores + acks).
 *
 * FAILURE MUST BE VISIBLE. This used to `await fetch(...)` inside a bare
 * try/catch, which only notices a network-level failure: a 400, 403, 413 or 429
 * resolves normally, so a rejected upload was indistinguishable from a
 * successful one. The command then sat at "sent" forever and the fleet console
 * simply showed nothing - which is exactly what happened on the Samsung bench
 * on 2026-08-12, where "Capture logs" reported SENT and no artifact ever
 * appeared, with no way to tell whether the panel had never received the
 * command, never captured, or been refused at the door.
 *
 * So the status is checked, the reason is written into the ring buffer (where
 * the NEXT capture will carry it), and the command is acked with an `error`
 * result. An acked-with-error command renders in the console as a failure with
 * a cause instead of a silence, which is the whole difference between a bug
 * that can be diagnosed from a desk and one that costs a trip to the panel.
 */
async function upload(
  args: UploadArgs,
  capture: Capture,
  filename: string
): Promise<void> {
  const form = new FormData();
  // BEFORE the file, deliberately: the server reads this off `file.fields`, which
  // only carries the parts multipart already streamed past when it reached the
  // file. A field appended after the blob is parsed but never seen.
  form.append("captureMode", capture.mode);
  form.append("file", capture.blob, filename);
  let reason: string;
  try {
    const res = await fetch(
      `${args.apiBase ?? ""}/api/foyer/screens/${args.screenId}/commands/${args.commandId}/upload`,
      {
        method: "POST",
        headers: { "X-Screen-Token": args.screenToken },
        body: form,
      }
    );
    if (res.ok) return;
    // The body names which refusal it was (`no_file`, `wrong_screen`, …).
    // Bounded, because a proxy error page can be a whole HTML document.
    const body = await res.text().catch(() => "");
    reason = `HTTP ${res.status} ${body.slice(0, 200)}`.trim();
  } catch (err) {
    reason = err instanceof Error ? err.message : "network error";
  }
  console.warn(
    `[diagnostics] upload of ${filename} (${capture.blob.size} bytes) failed: ${reason}`
  );
  // Ack with the failure so the console stops saying "sent" and starts saying
  // why. Best-effort in turn - if even this cannot get out, the server's sweep
  // retires the command as before.
  await ackCommand({
    screenId: args.screenId,
    screenToken: args.screenToken,
    apiBase: args.apiBase,
    commandId: args.commandId,
    result: { error: reason, bytes: capture.blob.size, mode: capture.mode },
  });
}

/** Capture a screenshot and upload it (which acks the command). */
export async function runScreenshot(args: UploadArgs): Promise<void> {
  const stage = (name: string) => captureStage(args.commandId, name);
  stage("start");
  try {
    const capture = await captureScreenshot(stage);
    if (!capture) {
      // Every route failed, including the DOM render. This used to `return`
      // without a word, leaving the command at "sent" until a later drain acked
      // it blank - which the console read as success.
      await ackCommand({
        screenId: args.screenId,
        screenToken: args.screenToken,
        apiBase: args.apiBase,
        commandId: args.commandId,
        result: { error: "no route could capture the screen on this device" },
      });
      return;
    }
    // Name the file for what it holds. A JPEG called .png confuses every tool
    // that opens it by extension, including a support engineer's file browser.
    const ext = capture.blob.type === "image/jpeg" ? "jpg" : "png";
    stage("upload");
    await upload(args, capture, `${args.commandId}.${ext}`);
  } finally {
    captureDone(args.commandId);
  }
}

/** Capture recent logs and upload them (which acks the command). */
export async function runLogCapture(args: UploadArgs): Promise<void> {
  captureStage(args.commandId, "logs");
  try {
    const capture = await captureLogs();
    captureStage(args.commandId, "upload");
    await upload(args, capture, `${args.commandId}.log.txt`);
  } finally {
    captureDone(args.commandId);
  }
}
